import { Box, Dialog, DialogContent, MenuItem, Select, Stack, TextField } from "@mui/material";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { prepareMoneyFlowRequestDto } from "@/api/hooks/stat-analytics-hooks";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { GetHandHistoryRequest, MoneyFlowRequestDto } from "@/shared/api";
import { useApi } from "@/shared/api/use-api";

type HandsHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  state: MoneyFlowRequestDto;
};

const initState: Omit<GetHandHistoryRequest, "query"> = {
  asHero: false,
  heroName: "",
  opponentName: "",
};

export const HandsHistoryModal = ({ isOpen, onClose, state }: HandsHistoryModalProps) => {
  const [isExportLoading, setIsExportLoading] = useState(false);
  const { statAnalyticsApi } = useApi();
  const { t } = useTranslation();
  const [data, setData] = useState(initState);

  const handleSubmit = async () => {
    if (isExportLoading) {
      return;
    }

    try {
      setIsExportLoading(true);
      const response = await statAnalyticsApi.exportHandHistories(
        {
          ...data,
          query: prepareMoneyFlowRequestDto(state),
        },
        {
          responseType: "blob",
        }
      );
      const contentDisposition = response?.headers["content-disposition"];
      if (contentDisposition) {
        const fileName = contentDisposition.split("filename=")[1].split(";")[0];
        const url = window.URL.createObjectURL(response.data as Blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (e) {
      const message =
        (e as unknown as AxiosError<{ message: string }>)?.response?.data?.message || e;

      enqueueSnackbar(message as string, {
        variant: "error",
        autoHideDuration: 2000,
      });
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleClose = () => {
    if (isExportLoading) {
      return;
    }
    setData(initState);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogContent>
        <Stack
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px" /* Расстояние между элементами */,
            width: "500px" /* Ширина формы */,
          }}
        >
          <Select
            value={data.asHero ? "true" : "false"}
            size="small"
            onChange={(e) => {
              setData((prevState) => {
                return {
                  ...prevState,
                  asHero: e.target.value === "true",
                };
              });
            }}
            sx={{
              height: 32,
              width: "160px",
              "& .MuiSelect-select": { padding: "4px 8px" },
            }}
          >
            <MenuItem value="false">{t("asIs")}</MenuItem>
            <MenuItem value="true">{t("withHero")}</MenuItem>
          </Select>
          {data.asHero && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                margin="dense"
                label={t("heroName")}
                value={data.heroName}
                onChange={(e) => {
                  setData((prevState) => {
                    return {
                      ...prevState,
                      heroName: e.target.value,
                    };
                  });
                }}
                size="small"
                fullWidth
                variant="outlined"
              />
              <TextField
                margin="dense"
                label={t("opponentPrefix")}
                value={data.opponentName}
                onChange={(e) => {
                  setData((prevState) => {
                    return {
                      ...prevState,
                      opponentName: e.target.value,
                    };
                  });
                }}
                size="small"
                fullWidth
                variant="outlined"
              />
            </Box>
          )}

          <ModalActionsButtons
            onCancelClick={handleClose}
            disabled={isExportLoading}
            onConfirmClick={handleSubmit}
            loading={isExportLoading}
            confirmLabel={t("export")}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
